<script setup lang="ts">
import { getUrlParamsFromCriteria } from '@@/shared/property/url-serp';
import type { LocationApiSearchResult } from '@@/shared/location/types';
import type { AddressRetrieve } from '@@/shared/address/types';
import type { Dictionary } from '@@/shared/application';

const localePath = useLocalePath();
const { locale } = useI18n();
const route = useRoute();
const { propertySearchCriteria } = usePropertySearchCriteria(route);

const homeTabType = ref<'sale' | 'rental' | 'estimate'>('sale');
type SaleEntryType = 'used' | 'new' | 'prestige' | 'overseas' | 'land' | 'business' | 'building';
const selectedSaleSubEntry = ref<SaleEntryType>('used');
type RentalEntryType = 'tenant' | 'owner';
const selectedRentalSubEntry = ref<RentalEntryType>('tenant');
const selectedLocation = ref<LocationApiSearchResult>();

const updatedUrlParams = computed(() => {
  if (homeTabType.value === 'estimate') return;
  const updatedCriteria = {
    ...propertySearchCriteria.value,
    transactionType: homeTabType.value,
    ...selectedLocation.value ? { locations: [selectedLocation.value.slug] } : {},
  };

  if (homeTabType.value === 'sale') {
    if (selectedSaleSubEntry.value === 'used') updatedCriteria.projectTypes = ['used'];
    else if (selectedSaleSubEntry.value === 'new') updatedCriteria.projectTypes = ['new'];
    else if (selectedSaleSubEntry.value === 'land') updatedCriteria.propertyTypes = ['land'];
    else if (selectedSaleSubEntry.value === 'business') updatedCriteria.propertyTypes = ['business'];
    else if (selectedSaleSubEntry.value === 'building') updatedCriteria.propertyTypes = ['building'];
  }
  return getUrlParamsFromCriteria(locale.value, updatedCriteria);
});
const updatedSerpSlug = toRef(() => updatedUrlParams.value?.serpSlug);

// Updated locations
async function onLocationSelect(location?: LocationApiSearchResult) {
  selectedLocation.value = location;
  if (!location) return;
  trackSelection();
  trackSearchInput(location.displayNameWithCode);
  await navigateTo(localePath({
    name: 'serp-serpSlug',
    params: { serpSlug: updatedSerpSlug.value },
  }, locale.value));
}

// Updated address
async function onAddressRetrieveSelect(address?: AddressRetrieve) {
  if (!address) return;
  trackSelection();
  trackSearchInput(address.fullAddress);
  if (homeTabType.value === 'rental' && selectedRentalSubEntry.value === 'owner') {
    window.open('https://www.homepilot.fr/', '_blank');
    return;
  }
  await navigateTo(localePath({
    name: 'valuation-online-valuation',
    query: {
      addressId: address.id,
      returnTo: route.path,
    },
  }, locale.value));
}

const { t } = useI18n();

const saleEntryValues = computed<{ value: SaleEntryType, label: string }[]>(() => {
  return [
    {
      value: 'used',
      label: t('property_serp.filters.project_type.used'),
    },
    {
      value: 'new',
      label: t('property.tags.new-build'),
    },
    {
      value: 'overseas',
      label: t('core.header.menu.overseas'),
    },
    {
      value: 'land',
      label: t('property.types_and_sub_types.land'),
    },
    {
      value: 'business',
      label: t('core.header.menu.commercial'),
    },
    {
      value: 'building',
      label: t('property.types_and_sub_types.building'),
    }];
});
const rentalEntryValues = computed<{ value: RentalEntryType, label: string }[]>(() => {
  return [{
    value: 'tenant',
    label: t('homepage.tenant'),
  },
  {
    value: 'owner',
    label: t('homepage.owner'),
  }];
});
const OVERSEAS_TYPE_VALUES = ['ES', 'PT', 'IT', 'MX', 'DU', 'GR', 'CY'] as const;
type OverseasType = typeof OVERSEAS_TYPE_VALUES[number];
interface SelectOption { title: string, value: OverseasType };
const selectedOverseasEntry = ref<SelectOption>({
  title: t('references.countries.ES'),
  value: 'ES',
});

const overseasEntryValues = computed<SelectOption[]>(() => {
  return OVERSEAS_TYPE_VALUES.map((value) => {
    return {
      value,
      title: t(`references.countries.${value}`),
    };
  });
});

const OVERSEAS_TRANSLATED_URLS: Dictionary<OverseasType> = {
  ES: {
    fr: 'https://www.iadoverseas.com/fr/espagne/annonces',
    en: 'https://www.iadoverseas.com/spain/ads',
  },
  PT: {
    fr: 'https://www.iadoverseas.com/fr/portugal/annonces',
    en: 'https://www.iadoverseas.com/portugal/ads',
  },
  IT: {
    fr: 'https://www.iadoverseas.com/fr/italie/annonces',
    en: 'https://www.iadoverseas.com/italy/ads',
  },
  MX: {
    fr: 'https://www.iadoverseas.com/fr/mexique/annonces',
    en: 'https://www.iadoverseas.com/mexico/ads',
  },
  DU: {
    fr: 'https://lp.iadoverseas.com/overseas-acheter-a-dubai/',
    en: 'https://lp.iadoverseas.com/overseas-buy-in-dubai/',
  },
  GR: {
    fr: 'https://lp.iadoverseas.com/acheter-en-grece/',
    en: 'https://lp.iadoverseas.com/buy-in-greece/',
  },
  CY: {
    fr: 'https://lp.iadoverseas.com/acheter-a-chypre/',
    en: 'https://lp.iadoverseas.com/buy-in-cyprus/',
  },
};

async function onSelectOverseasCountry(selectedOverseas?: SelectOption) {
  if (!selectedOverseas) return;
  trackSelection();
  const redirectOverseasUrl = OVERSEAS_TRANSLATED_URLS[selectedOverseas.value][locale.value];
  window.open(redirectOverseasUrl, '_blank');
  selectedOverseasEntry.value = selectedOverseas;
}

function trackSelection() {
  trackEvent({
    event: 'menu_interaction',
    event_name: 'search_bar_click',
    menu_category1: homeTabType.value,
    menu_category2: homeTabType.value === 'sale'
      ? selectedSaleSubEntry.value
      : homeTabType.value === 'rental'
        ? selectedRentalSubEntry.value
        : undefined,
  });
}
function trackSearchInput(searchTerm: string) {
  trackEvent({
    event: 'search',
    event_name: 'search_interaction',
    search_location: 'home',
    search_type: 'property',
    search_term: searchTerm,
  });
}
</script>

<template>
  <form
    class="relative"
    autocomplete="off"
  >
    <div class="flex justify-center">
      <IadRadioGroup
        v-model="homeTabType"
        hide-details
      >
        <IadRadioTab
          :label="$t(`core.navigation.buy`)"
          value="sale"
        />
        <IadRadioTab
          :label="$t(`core.navigation.rent`)"
          value="rental"
        />
        <IadRadioTab
          :label="$t(`core.navigation.sell_estimate`)"
          value="estimate"
        />
      </IadRadioGroup>
    </div>
    <div
      class="bg-white max-w-4xl mx-auto flex flex-col gap-s px-m py-m md:px-xl md:py-l rounded-t-2xl
      group-[.is-prestige]:rounded-none group-[.is-prestige]:bg-[transparent]
      group-[.is-prestige]:px-m group-[.is-prestige]:md:px-0
      group-[.is-prestige]:py-m"
    >
      <IadRadioGroup
        v-if="homeTabType === 'sale'"
        v-model="selectedSaleSubEntry"
        class="group-[.is-prestige]:hidden"
        scrollable
      >
        <IadRadio
          v-for="{ value, label } of saleEntryValues"
          :key="value"
          :value
          :label
        />
      </IadRadioGroup>
      <IadRadioGroup
        v-else-if="homeTabType === 'rental'"
        v-model="selectedRentalSubEntry"
        class="group-[.is-prestige]:hidden"
        scrollable
      >
        <IadRadio
          v-for="{ value, label } of rentalEntryValues"
          :key="value"
          :value
          :label
        />
      </IadRadioGroup>
      <p
        v-if="homeTabType === 'rental' && selectedRentalSubEntry === 'owner'"
        class="font-semibold group-[.is-prestige]:hidden"
        v-text="$t(`homepage.optimize_investment_property`)"
      />
      <p
        v-else-if="homeTabType === 'sale' && selectedSaleSubEntry === 'overseas'"
        class="font-semibold group-[.is-prestige]:hidden"
        v-text="$t(`homepage.find_abroad_dream_property`)"
      />
      <p
        v-else-if="homeTabType === 'estimate'"
        class="font-semibold group-[.is-prestige]:hidden"
        v-text="$t(`homepage.valuation_intro`)"
      />
      <div class="w-full">
        <IadSelect
          v-if="homeTabType === 'sale' && selectedSaleSubEntry === 'overseas'"
          name="overseas"
          :model-value="selectedOverseasEntry"
          :items="overseasEntryValues"
          @update:model-value="onSelectOverseasCountry"
        />
        <IadInputLocation
          v-else-if="homeTabType === 'sale' || (homeTabType === 'rental' && selectedRentalSubEntry === 'tenant')"
          class="flex-1 pb-l"
          :placeholder="$t(`core.location.input_placeholder`)"
          @update:model-value="onLocationSelect"
        />
        <IadInputAddress
          v-else-if="homeTabType === 'estimate' || (homeTabType === 'rental' && selectedRentalSubEntry === 'owner') "
          class="flex-1"
          :placeholder="$t(`core.location.input_address_placeholder`)"
          @update:model-value="onAddressRetrieveSelect"
        />
      </div>
    </div>
  </form>
</template>
